import styled, { css } from 'styled-components';

/**
 * Layout components for page structure
 * Usage:
 * <Container>
 *   <PageHeader>
 *     <PageTitle>Page Title</PageTitle>
 *   </PageHeader>
 *   <ContentWrapper>
 *     <MainContent>Main content here</MainContent>
 *     <Sidebar>Sidebar content</Sidebar>
 *   </ContentWrapper>
 * </Container>
 */

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0 0.5rem;
  }
  
  ${props => props.fluid && css`
    max-width: none;
  `}
  
  ${props => props.narrow && css`
    max-width: 800px;
  `}
`;

export const PageContainer = styled.div`
  min-height: calc(100vh - 60px);
  padding: 2rem 0;
  position: relative;
  z-index: 1;
  
  ${props => props.centered && css`
    display: flex;
    align-items: center;
    justify-content: center;
  `}
  
  ${props => props.background && css`
    background-color: ${props.background};
  `}
`;

export const ContentWrapper = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const MainContent = styled.main`
  flex: 1;
`;

export const Sidebar = styled.aside`
  width: 300px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
  
  ${props => props.width && css`
    width: ${props.width};
    
    @media (max-width: 768px) {
      width: 100%;
    }
  `}
`;

export const PageHeader = styled.header`
  margin-bottom: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.15);
  position: relative;
  overflow: hidden;
  
  /* Space-inspired background with subtle gradient and stars */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(124, 58, 237, 0.1) 0%, transparent 50%);
    z-index: -1;
  }
  
  /* Animated subtle star effect */
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: 
      radial-gradient(1px 1px at 10% 10%, rgba(255, 255, 255, 0.9) 1px, transparent 0),
      radial-gradient(1px 1px at 30% 40%, rgba(255, 255, 255, 0.8) 1px, transparent 0),
      radial-gradient(1px 1px at 50% 20%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1px 1px at 70% 60%, rgba(255, 255, 255, 0.8) 1px, transparent 0),
      radial-gradient(1px 1px at 90% 30%, rgba(255, 255, 255, 0.9) 1px, transparent 0);
    z-index: -1;
    opacity: 0.5;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.25rem;
    padding: 1.25rem;
  }
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-size: 2.25rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 0 15px rgba(99, 102, 241, 0.5);
  letter-spacing: 0.5px;
  position: relative;
  
  /* Subtle glass underline effect */
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 60%;
    height: 2px;
    background: linear-gradient(90deg, 
      rgba(255, 255, 255, 0.7), 
      rgba(99, 102, 241, 0.5),
      rgba(255, 255, 255, 0)
    );
    border-radius: 1px;
  }
`;

export const PageSubtitle = styled.p`
  margin: 0.5rem 0 0 0;
  font-size: 1rem;
  color: #5F6368;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 12}, 1fr);
  gap: ${props => props.gap || '1rem'};
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(${props => props.mobileColumns || 1}, 1fr);
  }
`;

export const GridItem = styled.div`
  grid-column: span ${props => props.span || 1};
  
  @media (max-width: 768px) {
    grid-column: span ${props => props.mobileSpan || props.span || 1};
  }
`;

export const Flex = styled.div`
  display: flex;
  gap: ${props => props.gap || '1rem'};
  flex-wrap: ${props => props.wrap || 'nowrap'};
  align-items: ${props => props.alignItems || 'center'};
  justify-content: ${props => props.justifyContent || 'flex-start'};
  
  @media (max-width: 768px) {
    flex-direction: ${props => props.mobileFlex || 'column'};
  }
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  margin: 2rem 0;
`;

export const Section = styled.section`
  margin-bottom: 2.5rem;
`;

export const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #333;
`;

// Export named components grouped in an object
const LayoutComponents = {
  Container,
  PageContainer,
  ContentWrapper,
  MainContent,
  Sidebar,
  PageHeader,
  PageTitle,
  PageSubtitle,
  Grid,
  GridItem,
  Flex,
  Divider,
  Section,
  SectionTitle
};

export default LayoutComponents;
